import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  EmailTemplate,
  EmailTemplateListParams,
  HookParams,
  SMSTemplate,
  SMSTemplateListParams,
} from '@/libs/interfaces'
import { communicationApi } from '@/services/api'

// Email Templates
export function useInfiniteEmailTemplates({
  params,
  enabled,
}: HookParams<EmailTemplateListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.communication.emailTemplates.infinite(params),
    queryFn: ({ pageParam }) =>
      communicationApi.listEmailTemplatesCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useEmailTemplate({
  params,
  enabled,
}: HookParams<{ orgID: string; emailTemplateID: string }>) {
  return useQuery({
    queryKey: QUERY_KEYS.communication.emailTemplates.id(params.emailTemplateID),
    queryFn: () => communicationApi.getEmailTemplate(params.orgID, params.emailTemplateID),
    enabled: (enabled ?? true) && !!params.emailTemplateID,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useCreateEmailTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (data: Partial<EmailTemplate>) => communicationApi.createEmailTemplate(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.communication.emailTemplates.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (payload: Partial<EmailTemplate>) => communicationApi.updateEmailTemplate(payload),
    onSuccess: (_data, payload) => {
      if (payload.emailTemplateID) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.communication.emailTemplates.id(payload.emailTemplateID),
        })
      }
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.communication.emailTemplates.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useDeleteEmailTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, emailTemplateID }: { orgID: string; emailTemplateID: string }) =>
      communicationApi.deleteEmailTemplate(orgID, emailTemplateID),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.communication.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

//SMS Templates
export function useInfiniteSMSTemplates({ params, enabled }: HookParams<SMSTemplateListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.communication.smsTemplates.infinite(params),
    queryFn: ({ pageParam }) =>
      communicationApi.listSMSTemplatesCursor({ ...params, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useSMSTemplate({
  params,
  enabled,
}: HookParams<{ orgID: string; smsTemplateID: string }>) {
  return useQuery({
    queryKey: QUERY_KEYS.communication.smsTemplates.id(params.smsTemplateID),
    queryFn: () => communicationApi.getSmsTemplate(params.orgID, params.smsTemplateID),
    enabled: (enabled ?? true) && !!params.smsTemplateID,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useCreateSMSTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (data: Partial<SMSTemplate>) => communicationApi.createSMSTemplate(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.communication.smsTemplates.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useUpdateSMSTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: (payload: Partial<SMSTemplate>) => communicationApi.updateSmsTemplate(payload),
    onSuccess: (_data, payload) => {
      if (payload.smsTemplateID) {
        void queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.communication.smsTemplates.id(payload.smsTemplateID),
        })
      }
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.communication.smsTemplates.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useDeleteSMSTemplate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, smsTemplateID }: { orgID: string; smsTemplateID: string }) =>
      communicationApi.deleteSMSTemplate(orgID, smsTemplateID),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.communication.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
